// src/components/Callbacks.js - Fixed to properly use name field
import React, { useState, useEffect } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import './Callbacks.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const Callbacks = () => {
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoScroll, setAutoScroll] = useState(false);
  const [intervalSeconds, setIntervalSeconds] = useState(10);
  const [showControls, setShowControls] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [callbacksEnabled, setCallbacksEnabled] = useState(true);
  const [selectedEventIds, setSelectedEventIds] = useState([]);
  const [competitionThumbnail, setCompetitionThumbnail] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [couples, setCouples] = useState([]);
  const [dancerLocationCache, setDancerLocationCache] = useState(new Map());
  const [displaySettings, setDisplaySettings] = useState(null);
  const { selectedCompetition } = useCompetition();

  // Auto-scroll interval ref
  const [intervalId, setIntervalId] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Fetch display settings to check if idle display is enabled
  const fetchDisplaySettings = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/display`);
      if (response.ok) {
        const data = await response.json();
        setDisplaySettings(data);
      }
    } catch (err) {
      console.error('Error fetching display settings:', err);
    }
  };

  // Load current event index from backend API
  const loadCurrentEventIndex = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/callbacks-index`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.currentEventIndex !== undefined) {
        setCurrentEventIndex(data.currentEventIndex);
        console.log('Loaded current event index from backend API:', data.currentEventIndex);
      }
    } catch (err) {
      console.error('Error loading current event index from backend:', err);
    }
  };

  // Save current event index to backend API
  const saveCurrentEventIndex = async (index) => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/callbacks-index`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentEventIndex: index }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.log('Saved current event index to backend API:', index);
    } catch (err) {
      console.error('Error saving current event index to backend:', err);
    }
  };

  // Function to fetch clubs from API
  const fetchClubs = async () => {
    try {
      // Fetch all clubs without competition filter (Callbacks is public display)
      const url = `https://${_host}:6003/api/${_company}/clubs`;

      const response = await fetch(url);
      if (response.ok) {
        const clubsData = await response.json();
        console.log('Callbacks - Fetched clubs:', clubsData.length, 'clubs');
        setClubs(clubsData);
      } else {
        console.warn('Callbacks - Failed to fetch clubs:', response.status);
      }
    } catch (err) {
      console.error('Callbacks - Error fetching clubs:', err);
    }
  };

  // Function to fetch couples (dancers) from API to get club associations
  const fetchCouples = async () => {
    try {
      const url = `https://${_host}:6003/api/${_company}/couples`;
      const response = await fetch(url);
      if (response.ok) {
        const couplesData = await response.json();
        console.log('Callbacks - Fetched couples:', couplesData.length, 'couples');
        setCouples(couplesData);
      } else {
        console.warn('Callbacks - Failed to fetch couples:', response.status);
      }
    } catch (err) {
      console.error('Callbacks - Error fetching couples:', err);
    }
  };

  // Get dancer location based on club - enhanced synchronous lookup with couples mapping
  const getDancerLocation = (dancer) => {
    // Support both naming conventions for cloth number
    const clothNumber = dancer.clothNumber || dancer.cloth_number;

    // Check cache first
    if (dancerLocationCache.has(clothNumber)) {
      const cachedResult = dancerLocationCache.get(clothNumber);
      return cachedResult;
    }

    // Start with dancer's country as default
    let location = dancer.country || 'Unknown';

    // Find the corresponding couple/dancer record to get club associations (both naming conventions)
    const coupleRecord = couples.find(couple => {
      const coupleClothNumber = couple.clothNumber || couple.cloth_number;
      const coupleId = couple.id || couple.couple_id;
      const dancerCoupleId = dancer.coupleId || dancer.couple_id;

      return coupleClothNumber === clothNumber || coupleId === dancerCoupleId;
    });

    if (coupleRecord) {
      console.log('Callbacks - Found couple record for dancer', clothNumber, ':', coupleRecord.name);

      // Try to find club by clubId from couple record (both naming conventions)
      let club = null;
      const clubId = coupleRecord.clubId || coupleRecord.club_id;
      if (clubId) {
        club = clubs.find(c => c.id === clubId);
      }

      // Try to find club by clubName from couple record (both naming conventions)
      if (!club) {
        const clubName = coupleRecord.clubName || coupleRecord.club_name;
        if (clubName) {
          club = clubs.find(c => c.name.toLowerCase() === clubName.toLowerCase());
        }
      }

      // Use club data if found
      if (club) {
        console.log('Callbacks - Found club for dancer', clothNumber, ':', club.name, 'Province:', club.province, 'Country:', club.country);
        if (club.province && club.country) {
          // Club has both province and country - show both
          location = `${club.province}, ${club.country}`;
        } else if (club.province) {
          // Club has province but no country - combine with dancer's country
          const country = dancer.country || club.country || '';
          location = country ? `${club.province}, ${country}` : club.province;
        } else if (club.country) {
          // Club has country but no province - use club country
          location = club.country;
        }
      } else {
        console.log('Callbacks - No club found for couple record:', coupleRecord);
      }
    } else {
      console.log('Callbacks - No couple record found for dancer:', clothNumber);
    }

    return location;
  };

  // Build dancer location cache from all events data using couples mapping
  const buildDancerLocationCache = () => {
    console.log('Callbacks - buildDancerLocationCache called. Clubs:', clubs.length, 'Couples:', couples.length, 'Events:', events.length);
    if (clubs.length === 0 || couples.length === 0 || events.length === 0) {
      console.log('Callbacks - Skipping cache build - missing data');
      return;
    }

    try {
      console.log('Callbacks - Building dancer location cache from all events...');
      const newCache = new Map();

      events.forEach((event, eventIndex) => {
        console.log(`Callbacks - Processing event ${eventIndex}: ${event.name} with ${event.dancers?.length || 0} dancers`);

        // Support both naming conventions for dancers array
        const dancers = event.dancers || event.dancers_list || [];
        if (Array.isArray(dancers)) {
          dancers.forEach(dancer => {
            // Support both naming conventions for cloth number
            const clothNumber = dancer.clothNumber || dancer.cloth_number;
            let location = dancer.country || 'Unknown';
            console.log(`Callbacks - Processing dancer ${clothNumber}, default location: ${location}`);

            // Find the corresponding couple/dancer record to get club associations (both naming conventions)
            const coupleRecord = couples.find(couple => {
              const coupleClothNumber = couple.clothNumber || couple.cloth_number;
              const coupleId = couple.id || couple.couple_id;
              const dancerCoupleId = dancer.coupleId || dancer.couple_id;

              return coupleClothNumber === clothNumber || coupleId === dancerCoupleId;
            });

            if (coupleRecord) {
              console.log(`Callbacks - Found couple record for dancer ${clothNumber}: ${coupleRecord.name}`);

              // Try to find club by clubId from couple record (both naming conventions)
              let club = null;
              const clubId = coupleRecord.clubId || coupleRecord.club_id;
              if (clubId) {
                club = clubs.find(c => c.id === clubId);
                console.log(`Callbacks - Looking for club by ID ${clubId}:`, club ? club.name : 'Not found');
              }

              // Try to find club by clubName from couple record (both naming conventions)
              if (!club) {
                const clubName = coupleRecord.clubName || coupleRecord.club_name;
                if (clubName) {
                  club = clubs.find(c => c.name.toLowerCase() === clubName.toLowerCase());
                  console.log(`Callbacks - Looking for club by name ${clubName}:`, club ? club.name : 'Not found');
                }
              }

              // Enhanced location logic - prioritize showing both province and country from club/studio
              if (club) {
                console.log(`Callbacks - Found club ${club.name} with province: ${club.province}, country: ${club.country}`);
                if (club.province && club.country) {
                  // Club has both province and country - show both
                  location = `${club.province}, ${club.country}`;
                  console.log(`Callbacks - Set location to province+country: ${location}`);
                } else if (club.province) {
                  // Club has province but no country - combine with dancer's country if available
                  const country = dancer.country || club.country || '';
                  location = country ? `${club.province}, ${country}` : club.province;
                  console.log(`Callbacks - Set location to province only: ${location}`);
                } else if (club.country) {
                  // Club has country but no province - use club country
                  location = club.country;
                  console.log(`Callbacks - Set location to club country: ${location}`);
                }
              } else {
                console.log(`Callbacks - No club found for couple record:`, coupleRecord);
                // Fallback to dancer country (already set as default)
                console.log(`Callbacks - Using fallback location: ${location}`);
              }
            } else {
              console.log(`Callbacks - No couple record found for dancer ${clothNumber}`);
            }

            // Cache the result using clothNumber as key (since dancers in callbacks use clothNumber)
            newCache.set(clothNumber, location);
            console.log(`Callbacks - Cached location for dancer ${clothNumber}: ${location}`);
          });
        }
      });

      console.log('Callbacks - Built location cache with', newCache.size, 'entries');
      console.log('Callbacks - Sample cache entries:');
      let sampleCount = 0;
      for (const [clothNumber, location] of newCache.entries()) {
        if (sampleCount < 5) {
          console.log(`  - ${clothNumber}: ${location}`);
          sampleCount++;
        }
      }
      setDancerLocationCache(newCache);
    } catch (error) {
      console.error('Callbacks - Error building dancer location cache:', error);
    }
  };

  // Fetch callbacks settings and events on component mount
  useEffect(() => {
    fetchEvents();
    fetchClubs();
    fetchCouples();
    loadCurrentEventIndex();
    fetchCallbacksSettings();
    fetchDisplaySettings(); // Fetch display settings to check idle display toggle

    // Simple polling for callbacks settings and display settings
    const pollInterval = setInterval(() => {
      fetchCallbacksSettings();
      fetchDisplaySettings();
    }, 5000); // Poll every 5 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, []);

  // Polling disabled to prevent conflicts with manual state updates
  // We now use manual refresh and forced filtering instead
  // useEffect(() => {
  //   // Clear any existing interval first
  //   const cleanup = () => {
  //     if (window.callbacksPollInterval) {
  //       clearInterval(window.callbacksPollInterval);
  //       window.callbacksPollInterval = null;
  //     }
  //   };

  //   cleanup();

  //   // Only poll frequently when callbacks are enabled
  //   if (callbacksEnabled) {
  //     window.callbacksPollInterval = setInterval(() => {
  //       fetchCallbacksSettings();
  //       fetchEvents();
  //     }, 5000);
  //   } else {
  //     // Poll much less frequently when disabled (just to check if re-enabled)
  //     window.callbacksPollInterval = setInterval(() => {
  //       fetchCallbacksSettings();
  //     }, 15000); // 15 seconds instead of 5
  //   }

  //   return cleanup;
  // }, [callbacksEnabled]);

  // Debug: Log current state changes
  useEffect(() => {
    console.log('Current callbacksEnabled state:', callbacksEnabled);
  }, [callbacksEnabled]);

  // Fetch callbacks settings from backend API
  const fetchCallbacksSettings = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/callbacks-settings`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const settings = await response.json();

      // Use snake_case field names from API response
      const selectedEventIds = settings.selected_event_ids || [];
      const autoScroll = settings.auto_scroll || false;

      setCallbacksEnabled(settings.enabled || false);
      setIntervalSeconds(settings.interval_seconds || 10);
      setSelectedEventIds(selectedEventIds);

      // Fix: Don't compute autoScroll, use it directly from settings
      setAutoScroll(autoScroll);

  
    } catch (err) {
      console.error('Error fetching callbacks settings from backend:', err);
    }
  };

  // Force event filtering check
  const forceEventFiltering = () => {
    console.log('🔥 FORCE FILTERING CHECK:');
    console.log('🔥 callbacksEnabled:', callbacksEnabled);
    console.log('🔥 selectedEventIds:', selectedEventIds);
    console.log('🔥 selectedEventIds.length:', selectedEventIds.length);
    console.log('🔥 allEvents.length:', allEvents.length);

    if (!callbacksEnabled) {
      console.log('🔥 BRANCH: callbacksEnabled false - clearing events');
      setEvents([]);
    } else if (selectedEventIds.length > 0) {
      console.log('🔥 BRANCH: callbacksEnabled true AND events selected');
      const filteredEvents = allEvents.filter(event =>
        selectedEventIds.includes(event.id)
      );
      console.log('🔥 Filtered events count:', filteredEvents.length);
      setEvents(filteredEvents);
    } else {
      console.log('🔥 BRANCH: callbacksEnabled true BUT no events selected');
      setEvents([]);
    }
  };

  // Handle auto-scrolling when enabled
  useEffect(() => {
    // Clear any existing intervals
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    if (autoScroll && events.length > 1) {
      let countdownValue = intervalSeconds;
      setCountdown(countdownValue);

      // Use a single timer to handle both countdown and event switching
      const interval = setInterval(() => {
        setCountdown(prev => {
          const newCountdown = prev - 1;

          // When countdown reaches 0, switch events and reset
          if (newCountdown <= 0) {
            setCurrentEventIndex((prevIndex) => {
              const newIndex = (prevIndex + 1) % events.length;
              console.log(`🔄 Auto-scrolling from index ${prevIndex} to ${newIndex} (${events.length} events)`);
              // Save to backend to persist across refreshes
              saveCurrentEventIndex(newIndex);
              return newIndex;
            });
            return intervalSeconds; // Reset countdown
          }

          return newCountdown;
        });
      }, 1000);

      setIntervalId(interval);

      return () => {
        clearInterval(interval);
      };
    } else {
      setCountdown(null); // Clear countdown when auto-scroll is disabled
    }
  }, [autoScroll, events.length, intervalSeconds]); // Removed 'events' from dependency to prevent re-renders

  // Update competition thumbnail when selected competition changes
  useEffect(() => {
    if (selectedCompetition && selectedCompetition.thumbnail) {
      setCompetitionThumbnail(selectedCompetition.thumbnail);
    } else {
      setCompetitionThumbnail(null);
    }
  }, [selectedCompetition]);

  // Rebuild location cache when clubs, couples, or events data changes
  useEffect(() => {
    console.log('Callbacks - Cache rebuild useEffect triggered. Clubs:', clubs.length, 'Couples:', couples.length, 'Events:', events.length);
    if (clubs.length > 0 && couples.length > 0 && events.length > 0) {
      console.log('Callbacks - Scheduling cache build in 1 second...');
      const timer = setTimeout(() => {
        console.log('Callbacks - Triggering cache build now...');
        buildDancerLocationCache();
      }, 1000); // Wait 1 second after data is loaded

      return () => {
        console.log('Callbacks - Clearing cache build timer');
        clearTimeout(timer);
      };
    } else {
      console.log('Callbacks - Not building cache - missing data');
    }
  }, [clubs, couples, events]);

  // Update filtered events when callbacks settings change
  useEffect(() => {
    if (!callbacksEnabled) {
      // Toggle OFF - show thumbnail (no events)
      setEvents([]);
    } else if (selectedEventIds.length === 0) {
      // Toggle ON but no events selected
      setEvents([]);
    } else {
      // Toggle ON with events selected
      const filteredEvents = allEvents.filter(event =>
        selectedEventIds.includes(event.id)
      );
      setEvents(filteredEvents);

      // Reset index if we have events and current index is invalid
      if (filteredEvents.length > 0 && currentEventIndex >= filteredEvents.length) {
        setCurrentEventIndex(0);
        saveCurrentEventIndex(0);
      }
    }
  }, [callbacksEnabled, selectedEventIds, allEvents]);

  // Reset current event index if it's out of bounds
  useEffect(() => {
    if (events.length > 0 && currentEventIndex >= events.length) {
      console.log(`⚠️ Index out of bounds: currentEventIndex=${currentEventIndex}, events.length=${events.length}. Resetting to 0.`);
      setCurrentEventIndex(0);
    }
  }, [events.length, currentEventIndex]);

  // Function to fetch all events
  const fetchEvents = async () => {
    try {
      if (loading) setLoading(true);

      const response = await fetch(`https://${_host}:6003/api/${_company}/events`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      //console.log("OUT:",data)

      // Filter events that have dancers (couples)
      const eventsWithDancers = data.filter(event => event.dancers && event.dancers.length > 0);

      setAllEvents(eventsWithDancers);

      // Filter events based on selected event IDs if callbacks are enabled
      if (callbacksEnabled && selectedEventIds.length > 0) {
        const filteredEvents = eventsWithDancers.filter(event =>
          selectedEventIds.includes(event.id)
        );
        setEvents(filteredEvents);
      } else if (callbacksEnabled) {
        // If callbacks are enabled but no specific events selected, show all
        setEvents(eventsWithDancers);
      } else {
        // If callbacks disabled, show no events
        setEvents([]);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Failed to connect to competition data. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions
  const goToNextEvent = () => {
    if (events.length > 1) {
      setCurrentEventIndex((prevIndex) => {
        const newIndex = (prevIndex + 1) % events.length;
        saveCurrentEventIndex(newIndex);
        return newIndex;
      });
    }
  };

  const goToPreviousEvent = () => {
    if (events.length > 1) {
      setCurrentEventIndex((prevIndex) => {
        const newIndex = prevIndex === 0 ? events.length - 1 : prevIndex - 1;
        saveCurrentEventIndex(newIndex);
        return newIndex;
      });
    }
  };

  const goToEvent = (index) => {
    if (index >= 0 && index < events.length) {
      setCurrentEventIndex(index);
      saveCurrentEventIndex(index);
    }
  };

  // Toggle auto-scroll
  const toggleAutoScroll = () => {
    setAutoScroll(!autoScroll);
  };

  // Handle interval change
  const handleIntervalChange = (newInterval) => {
    setIntervalSeconds(newInterval);
  };

  // Format dancer names using the name field directly
  const formatDancerNames = (dancer) => {
    // Use the name field directly as it should already be properly formatted
    if (dancer.name && dancer.name.trim()) {
      // Clean up any formatting issues
      let cleanedName = dancer.name
        .replace(/& undefined/g, '')  // Remove "& undefined" 
        .replace(/undefined &/g, '')  // Remove "undefined &"
        .replace(/undefined/g, '')    // Remove standalone "undefined"
        .replace(/\s+/g, ' ')         // Replace multiple spaces with single space
        .trim();                      // Remove leading/trailing whitespace
      
      // If after cleaning we still have a valid name, return it
      if (cleanedName && cleanedName !== '&') {
        return cleanedName;
      }
    }
    
    // Fallback: try to construct from individual fields if name is not available or invalid
    if (dancer.leadName && dancer.followName && dancer.followName !== 'undefined') {
      return `${dancer.leadName} & ${dancer.followName}`;
    } else if (dancer.leadName && dancer.leadName !== 'undefined') {
      return dancer.leadName;
    } else if (dancer.followName && dancer.followName !== 'undefined') {
      return dancer.followName;
    }
    
    // Final fallback
    return `Dancer ${dancer.clothNumber || 'Unknown'}`;
  };

  if (loading && events.length === 0) {
    return (
      <div className="callbacks-screen loading">
        <div className="loading-spinner"></div>
        <h2>Loading callbacks data...</h2>
      </div>
    );
  }

  if (error && events.length === 0) {
    return (
      <div className="callbacks-screen error">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={() => { fetchCallbacksSettings(); fetchEvents(); }} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  // Check if idle display is enabled (show background only)
  const showIdleDisplay = displaySettings && (displaySettings.showIdleDisplay === true || displaySettings.show_idle_display === true);

  if (showIdleDisplay) {
    return (
      <div className="callbacks-screen idle-background-only">
        {competitionThumbnail ? (
          <div className="competition-thumbnail-container">
            <img
              src={competitionThumbnail}
              alt="Competition Thumbnail"
              className="competition-thumbnail"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ) : (
          <div className="no-thumbnail-placeholder">
            <h1>Flokraft</h1>
            <p>Background Display Mode</p>
            <div className="idle-animation"></div>
          </div>
        )}
      </div>
    );
  }

  if (!callbacksEnabled) {
    return (
      <div className="callbacks-screen disabled">
        <div className="disabled-content">
          {competitionThumbnail ? (
            <div className="competition-thumbnail-container">
              <img
                src={competitionThumbnail}
                alt="Competition Thumbnail"
                className="competition-thumbnail"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="no-thumbnail-placeholder">
              <div className="placeholder-icon">🏆</div>
              <h2>Competition</h2>
              {selectedCompetition && (
                <p className="competition-name">{selectedCompetition.name}</p>
              )}
            </div>
          )}
          <div className="disabled-message">
            <p>Callbacks display is currently disabled</p>
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="callbacks-screen empty">
        <h1>No Events Available</h1>
        <p>Either no events have dancers, or no events have been selected for callbacks display.</p>
        <button onClick={() => { fetchCallbacksSettings(); fetchEvents(); }} className="refresh-btn">
          Refresh
        </button>
      </div>
    );
  }

  // Get current event to display
  const currentEvent = events[currentEventIndex];

  return (
    <div className="callbacks-screen">
      {/* Controls Panel */}
      {showControls && (
        <div className="callbacks-controls">
          <div className="controls-left">
            <button 
              className="control-btn"
              onClick={() => setShowControls(false)}
              title="Hide controls"
            >
              ×
            </button>
            <button
              className="control-btn refresh-btn"
              onClick={() => { fetchCallbacksSettings(); fetchEvents(); }}
              title="Refresh events"
            >
              🔄
            </button>
          </div>
          
          <div className="controls-center">
            {events.length > 1 && (
              <>
                <button 
                  className="nav-btn"
                  onClick={goToPreviousEvent}
                  title="Previous event"
                >
                  ←
                </button>
                
                <select 
                  className="event-selector"
                  value={currentEventIndex}
                  onChange={(e) => goToEvent(parseInt(e.target.value))}
                >
                  {events.map((event, index) => (
                    <option key={event.id || index} value={index}>
                      {event.name} ({event.dancers?.length || 0} dancers)
                    </option>
                  ))}
                </select>
                
                <button 
                  className="nav-btn"
                  onClick={goToNextEvent}
                  title="Next event"
                >
                  →
                </button>
              </>
            )}
          </div>
          
          <div className="controls-right">
            {events.length > 1 && (
              <>
                <label className="auto-scroll-control">
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={toggleAutoScroll}
                  />
                  Auto-scroll
                </label>
                
                {autoScroll && (
                  <select 
                    className="interval-selector"
                    value={intervalSeconds}
                    onChange={(e) => handleIntervalChange(parseInt(e.target.value))}
                  >
                    <option value={5}>5s</option>
                    <option value={10}>10s</option>
                    <option value={15}>15s</option>
                    <option value={30}>30s</option>
                    <option value={60}>1m</option>
                  </select>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Show controls button when hidden */}
      {!showControls && (
        <button 
          className="show-controls-btn"
          onClick={() => setShowControls(true)}
          title="Show controls"
        >
          ⚙️
        </button>
      )}

      {/* Connection status indicator */}
      {error && (
        <div className="connection-status error">
          <span>⚠️ Connection issue - trying to reconnect...</span>
        </div>
      )}

      <header className="callbacks-header">
        <h1 className="event-title">{currentEvent.name}</h1>
        <h2 className="event-round">{currentEvent.roundType} - Callbacks</h2>
        <div className="event-info">
          <div className="event-counter">
            Event {currentEventIndex + 1} of {events.length}
          </div>
          <div className="dancer-count">
            {currentEvent.dancers?.length || 0} Dancers
          </div>
          {autoScroll && (
            <div className="auto-scroll-indicator">
              <span className="pulse-dot"></span>
              Auto-scrolling
            </div>
          )}
        </div>
      </header>

      <div className="callbacks-content">
        <table className="callbacks-table">
          <thead>
            <tr>
              <th className="number-column">Number</th>
              <th className="name-column">Dancer(s)</th>
              <th className="location-column">Location</th>
            </tr>
          </thead>
          <tbody>
            {currentEvent.dancers
              ?.sort((a, b) => parseInt(a.clothNumber) - parseInt(b.clothNumber))
              .map((dancer, index) => (
                <tr
                  key={dancer.clothNumber}
                  className="dancer-row"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <td className="number-cell">
                    <span className="number-badge">{dancer.clothNumber}</span>
                  </td>
                  <td className="name-cell">{formatDancerNames(dancer)}</td>
                  <td className="location-cell">{getDancerLocation(dancer)}</td>
                </tr>
              )) || []}
          </tbody>
        </table>
      </div>

      <footer className="callbacks-footer">
        <div className="footer-left">
          <div className="logo">FLOKRAFT SCRUTINY</div>
        </div>
        <div className="footer-center">
          {countdown !== null && autoScroll && (
            <div className="countdown-display">
              <div className="countdown-circle">
                <div className="countdown-number">{countdown}</div>
              </div>
              <div className="countdown-text">Next Event</div>
            </div>
          )}
          {events.length > 1 && autoScroll && (
            <div className="next-event-display">
              
              <div className="next-event-name">
                {events[(currentEventIndex + 1) % events.length]?.name || 'Loading...'}
              </div>
            </div>
          )}
          {events.length > 1 && !autoScroll && (
            <div className="event-dots">
              {events.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentEventIndex ? 'active' : ''}`}
                  onClick={() => goToEvent(index)}
                  title={`Go to ${events[index]?.name}`}
                />
              ))}
            </div>
          )}
        </div>
        <div className="footer-right">
          <div className="timestamp">{new Date().toLocaleTimeString()}</div>
        </div>
      </footer>
    </div>
  );
};

export default Callbacks;